import { useState } from 'react';
import { useToastNotifications } from '@nbirl/nbi-common-ui-libs';

import { useErrorToast } from '../../../../../../shared/hooks';
import { DocumentStatus, SecureDocument } from '../../../models';
import { useDeleteDocumentMutation } from '../../../queries';
import { DeleteDocumentModal } from './delete-document-modal.component';
import { DocumentDropdownMenu } from './document-dropdown-menu.component';
import { MoveDocumentModal } from './move-document-modal.component';

type DocumentDropdownProps = {
  selectedDocument: SecureDocument;
  parentFolderId: string;
  creatorId: string;
  showInFolderItem: boolean;
  documentStatus?: DocumentStatus;
  onMoveDocumentSuccess: () => void;
  onShareDocument: () => void;
  onDeleteDocumentSuccess: () => void;
  onFolderChange: (id: string) => void;
  onDocumentStatusUpdateStart: (documentId: string) => void;
  onDocumentStatusUpdateFinish: (documentId: string) => void;
};

export const DocumentDropdown = ({
  parentFolderId,
  selectedDocument,
  documentStatus,
  creatorId,
  onMoveDocumentSuccess,
  onDeleteDocumentSuccess,
  showInFolderItem,
  onDocumentStatusUpdateStart,
  onDocumentStatusUpdateFinish,
  onFolderChange,
  onShareDocument,
}: DocumentDropdownProps) => {
  const { addToast } = useToastNotifications();
  const { showErrorToast } = useErrorToast();
  const [showDeleteDocumentModal, setShowDeleteDocumentModal] =
    useState<boolean>(false);
  const [showMoveDocumentModal, setShowMoveDocumentModal] =
    useState<boolean>(false);
  const deleteDocumentMutation = useDeleteDocumentMutation();

  const handleDocumentDelete = () => {
    setShowDeleteDocumentModal(false);
    deleteDocumentMutation.mutate(
      { documentId: selectedDocument.id },
      {
        onSuccess: () => {
          addToast({
            variant: 'success',
            text: 'Document successfully deleted.',
          });
          onDeleteDocumentSuccess();
        },
        onError: () => {
          showErrorToast('Document could not be deleted. Please try again.');
        },
      }
    );
  };

  return (
    <>
      <DocumentDropdownMenu
        selectedDocument={selectedDocument}
        documentStatus={documentStatus}
        parentFolderId={parentFolderId}
        showInFolderItem={showInFolderItem}
        isDeleteDocumentPending={deleteDocumentMutation.isLoading}
        creatorId={creatorId}
        onDocumentStatusUpdateStart={onDocumentStatusUpdateStart}
        onDocumentStatusUpdateFinish={onDocumentStatusUpdateFinish}
        onFolderChange={onFolderChange}
        onShareDocument={onShareDocument}
        onShowDeleteDocumentModal={() => setShowDeleteDocumentModal(true)}
        onShowMoveDocumentModal={() => setShowMoveDocumentModal(true)}
      />

      <MoveDocumentModal
        isOpen={showMoveDocumentModal}
        document={selectedDocument}
        onDismiss={() => setShowMoveDocumentModal(false)}
        onSuccess={onMoveDocumentSuccess}
      />

      <DeleteDocumentModal
        isOpen={showDeleteDocumentModal}
        onCloseModal={() => setShowDeleteDocumentModal(false)}
        onDeleteDocument={handleDocumentDelete}
      />
    </>
  );
};
